import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Typography,
  Chip,
  Stack,
  useTheme,
  alpha,
  Tooltip,
  TextField,
  InputAdornment,
  CircularProgress,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CheckCircle as PostedIcon,
  Schedule as DraftIcon,
  Warehouse as WarehouseIcon,
  Store as BranchIcon,
  PlaylistAddCheck as OpeningIcon,
  PostAdd as PostIcon,
  Undo as UnpostIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { InventoryOpeningBalance } from '../../types/inventoryOpeningBalances.types';
import { inventoryOpeningBalancesService } from '../../services/inventoryOpeningBalances.service';
import InventoryOpeningBalanceDialog from '../../components/inventoryOpeningBalances/InventoryOpeningBalanceDialog';

const translations = {
  en: {
    title: 'Inventory Opening Balances',
    subtitle: 'Manage opening inventory for warehouses and branches',
    add: 'New Opening Balance',
    documentNumber: 'Document #',
    date: 'Date',
    location: 'Location',
    totalValue: 'Total Value',
    status: 'Status',
    actions: 'Actions',
    posted: 'Posted',
    draft: 'Draft',
    search: 'Search...',
    noData: 'No opening balances found',
    confirmDelete: 'Are you sure you want to delete this document?',
    confirmPost: 'Post this document? Stock movements will be created.',
    confirmUnpost: 'Unpost this document? Stock movements will be reversed.',
    deleteSuccess: 'Deleted successfully',
    deleteFailed: 'Delete failed',
    postSuccess: 'Posted successfully',
    postFailed: 'Post failed',
    unpostSuccess: 'Unposted successfully',
    unpostFailed: 'Unpost failed',
    post: 'Post',
    unpost: 'Unpost',
    edit: 'Edit',
    delete: 'Delete',
    warehouse: 'Warehouse',
    branch: 'Branch',
    totalDocuments: 'Total Documents',
    totalPosted: 'Posted',
    totalDraft: 'Draft',
    totalAmount: 'Total Amount',
    cancel: 'Cancel',
  },
  ar: {
    title: 'الأرصدة الافتتاحية للمخزون',
    subtitle: 'إدارة المخزون الافتتاحي للمخازن والفروع',
    add: 'سند افتتاحي جديد',
    documentNumber: 'رقم السند',
    date: 'التاريخ',
    location: 'الموقع',
    totalValue: 'إجمالي القيمة',
    status: 'الحالة',
    actions: 'الإجراءات',
    posted: 'مرحّل',
    draft: 'مسودة',
    search: 'بحث...',
    noData: 'لا توجد سندات افتتاحية',
    confirmDelete: 'هل أنت متأكد من حذف هذا السند؟',
    confirmPost: 'ترحيل هذا السند؟ سيتم إنشاء حركات المخزون.',
    confirmUnpost: 'إلغاء ترحيل هذا السند؟ سيتم عكس حركات المخزون.',
    deleteSuccess: 'تم الحذف بنجاح',
    deleteFailed: 'فشل الحذف',
    postSuccess: 'تم الترحيل بنجاح',
    postFailed: 'فشل الترحيل',
    unpostSuccess: 'تم إلغاء الترحيل بنجاح',
    unpostFailed: 'فشل إلغاء الترحيل',
    post: 'ترحيل',
    unpost: 'إلغاء الترحيل',
    edit: 'تعديل',
    delete: 'حذف',
    warehouse: 'مخزن',
    branch: 'فرع',
    totalDocuments: 'إجمالي السندات',
    totalPosted: 'مرحّلة',
    totalDraft: 'مسودات',
    totalAmount: 'إجمالي المبالغ',
    cancel: 'إلغاء',
  },
};

const InventoryOpeningBalancesPage: React.FC = () => {
  const { language } = useLanguage();
  const theme = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';

  const { userBranchIds, userWarehouseIds } = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return { userBranchIds: [] as number[], userWarehouseIds: [] as number[] };
      const payload = JSON.parse(atob(token.split('.')[1]));
      const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
      const allowedBranches = payload.AllowedBranchId
        ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
        : [];
      const branchIds = Array.from(new Set([...primaryBranch, ...allowedBranches]));
      const keeperClaims = payload.KeeperWarehouseId;
      const warehouseIds = Array.isArray(keeperClaims) ? keeperClaims.map(Number) : keeperClaims ? [Number(keeperClaims)] : [];
      return { userBranchIds: branchIds, userWarehouseIds: warehouseIds };
    } catch {
      return { userBranchIds: [] as number[], userWarehouseIds: [] as number[] };
    }
  })();

  const canEditOpeningBalance = (balance: InventoryOpeningBalance) => {
    if (userBranchIds.length === 0 && userWarehouseIds.length === 0) return true;
    if (balance.warehouseId && userWarehouseIds.includes(balance.warehouseId)) return true;
    if (balance.branchId && userBranchIds.includes(balance.branchId)) return true;
    return false;
  };

  const canPostOpeningBalance = () => userBranchIds.length === 0 && userWarehouseIds.length === 0;

  const [openingBalances, setOpeningBalances] = useState<InventoryOpeningBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => { companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {}); }, []);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBalance, setSelectedBalance] = useState<InventoryOpeningBalance | undefined>(undefined);

  // ✅ دايلوج التأكيد الموحد
  type ConfirmAction = 'delete' | 'post' | 'unpost';
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean; action: ConfirmAction; balance: InventoryOpeningBalance | null;
  }>({ open: false, action: 'delete', balance: null });

  const openConfirm = (action: ConfirmAction, balance: InventoryOpeningBalance) =>
    setConfirmDialog({ open: true, action, balance });

  const closeConfirm = () => setConfirmDialog(prev => ({ ...prev, open: false }));

  const handleConfirm = async () => {
    const { action, balance } = confirmDialog;
    if (!balance) return;
    closeConfirm();
    try {
      if (action === 'delete') {
        await inventoryOpeningBalancesService.delete(balance.openingBalanceId);
        toast.success(t.deleteSuccess);
      } else if (action === 'post') {
        await inventoryOpeningBalancesService.post(balance.openingBalanceId);
        toast.success(t.postSuccess);
      } else if (action === 'unpost') {
        await inventoryOpeningBalancesService.unpost(balance.openingBalanceId);
        toast.success(t.unpostSuccess);
      }
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Operation failed');
    }
  };

  const confirmConfig: Record<ConfirmAction, {
    gradientFrom: string; gradientTo: string;
    icon: React.ReactNode;
    buttonColor: 'error' | 'success' | 'warning';
    message: string; buttonLabel: string;
  }> = {
    delete:  { gradientFrom: '#d32f2f', gradientTo: '#ef5350', icon: <DeleteIcon />, buttonColor: 'error',   message: t.confirmDelete,  buttonLabel: t.delete },
    post:    { gradientFrom: '#1b5e20', gradientTo: '#2e7d32', icon: <PostIcon />,   buttonColor: 'success', message: t.confirmPost,    buttonLabel: t.post },
    unpost:  { gradientFrom: '#e65100', gradientTo: '#f57c00', icon: <UnpostIcon />, buttonColor: 'warning', message: t.confirmUnpost,  buttonLabel: t.unpost },
  };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await inventoryOpeningBalancesService.getAll();
      const filteredData = data.filter((balance: InventoryOpeningBalance) => canEditOpeningBalance(balance));
      setOpeningBalances(filteredData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => { setSelectedBalance(undefined); setDialogOpen(true); };
  const handleEdit = (balance: InventoryOpeningBalance) => { setSelectedBalance(balance); setDialogOpen(true); };

  const filteredBalances = openingBalances.filter((balance) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      balance.documentNumber.toLowerCase().includes(searchLower) ||
      balance.warehouseName?.toLowerCase().includes(searchLower) ||
      balance.warehouseNameAr?.includes(searchTerm) ||
      balance.branchName?.toLowerCase().includes(searchLower) ||
      balance.branchNameAr?.includes(searchTerm)
    );
  });

  const stats = {
    total: openingBalances.length,
    posted: openingBalances.filter(b => b.status === 'Posted').length,
    draft: openingBalances.filter(b => b.status === 'Draft').length,
    totalAmount: openingBalances.reduce((sum, b) => sum + b.totalValue, 0),
  };

  const compName = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
  const pageTitle = isRTL ? 'سندات المخزون الافتتاحية' : 'Inventory Opening Balances';
  const fmt = (n: number) => n.toFixed(3);
  const fmtDate = (d: string) => new Date(d).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');

  const printBalance = (balance: InventoryOpeningBalance) => {
    const w = window.open('', '_blank', 'width=900,height=700'); if (!w) return;
    const loc = balance.warehouseName ? (isRTL ? balance.warehouseNameAr : balance.warehouseName) : (isRTL ? balance.branchNameAr : balance.branchName);
    w.document.write(`<html dir="${isRTL ? 'rtl' : 'ltr'}"><head><title>${balance.documentNumber}</title><style>
      body{font-family:'Segoe UI','Tajawal',sans-serif;margin:20px;color:#333}
      .header{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #1565C0;padding-bottom:10px;margin-bottom:15px}
      .header img{max-height:60px} .header h1{color:#1565C0;margin:0;font-size:20px}
      .info{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:15px;font-size:13px}
      .info b{color:#1565C0}
      table{width:100%;border-collapse:collapse;font-size:11px} th{background:#1565C0;color:white;padding:8px;text-align:${isRTL ? 'right' : 'left'}}
      td{padding:6px 8px;border:1px solid #ddd} .total{background:#e3f2fd;font-weight:bold}
      @media print{body{margin:10px}}
    </style></head><body>
    <div class="header"><div>${companyInfo?.logoBase64 ? `<img src="${companyInfo.logoBase64}"/>` : ''}</div>
    <div style="text-align:center;flex:1"><h1>${pageTitle}</h1><div style="color:#666;font-size:11px">${compName || ''}</div></div><div style="width:80px"></div></div>
    <div class="info"><div><b>${isRTL ? 'رقم السند:' : 'Document #:'}</b> ${balance.documentNumber}</div><div><b>${isRTL ? 'التاريخ:' : 'Date:'}</b> ${fmtDate(balance.documentDate)}</div>
    <div><b>${isRTL ? 'الموقع:' : 'Location:'}</b> ${loc || ''}</div><div><b>${isRTL ? 'الحالة:' : 'Status:'}</b> ${balance.status}</div>
    <div><b>${isRTL ? 'القيمة الإجمالية:' : 'Total Value:'}</b> ${fmt(balance.totalValue)}</div></div>
    <table><thead><tr><th>#</th><th>${isRTL ? 'كود الصنف' : 'Item Code'}</th><th>${isRTL ? 'الصنف' : 'Item'}</th><th>${isRTL ? 'الوحدة' : 'Unit'}</th><th>${isRTL ? 'الكمية' : 'Qty'}</th><th>${isRTL ? 'التكلفة' : 'Cost'}</th><th>${isRTL ? 'الإجمالي' : 'Total'}</th></tr></thead><tbody>
    ${balance.details.map((d, i) => `<tr><td>${i + 1}</td><td>${d.itemCode}</td><td>${isRTL ? d.itemNameAr : d.itemName}</td><td>${isRTL ? d.unitNameAr : d.unitName}</td><td>${fmt(d.quantity)}</td><td>${fmt(d.unitCost)}</td><td>${fmt(d.totalCost)}</td></tr>`).join('')}
    <tr class="total"><td colspan="6">${isRTL ? 'الإجمالي' : 'Total'}</td><td>${fmt(balance.totalValue)}</td></tr>
    </tbody></table><div style="text-align:center;margin-top:15px;color:#999;font-size:9px">${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div></body></html>`);
    w.document.close(); setTimeout(() => w.print(), 500);
  };

  const exportAllToExcel = async () => {
    try {
      const wb = new ExcelJS.Workbook(); const ws = wb.addWorksheet(pageTitle);
      if (companyInfo?.logoBase64) { try { const img = wb.addImage({ base64: companyInfo.logoBase64.split(',').pop() || companyInfo.logoBase64, extension: 'png' }); ws.addImage(img, { tl: { col: 0, row: 0 }, ext: { width: 100, height: 80 } }); } catch {} }
      ws.mergeCells('C1:F1'); const cc = ws.getCell('C1'); cc.value = compName || ''; cc.font = { size: 18, bold: true, color: { argb: 'FF1565C0' } }; cc.alignment = { horizontal: 'center', vertical: 'middle' };
      ws.mergeCells('A3:F3'); const tc = ws.getCell('A3'); tc.value = pageTitle; tc.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } }; tc.alignment = { horizontal: 'center' }; tc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
      const headers = [isRTL ? 'رقم السند' : 'Document #', isRTL ? 'التاريخ' : 'Date', isRTL ? 'الموقع' : 'Location', isRTL ? 'القيمة' : 'Value', isRTL ? 'الحالة' : 'Status'];
      const hr = ws.getRow(5); headers.forEach((h, i) => { const c = hr.getCell(i + 1); c.value = h; c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } }; c.alignment = { horizontal: 'center' }; });
      filteredBalances.forEach((b, i) => { const row = ws.getRow(6 + i); row.getCell(1).value = b.documentNumber; row.getCell(2).value = fmtDate(b.documentDate);
        row.getCell(3).value = b.warehouseName ? (isRTL ? b.warehouseNameAr : b.warehouseName) : (isRTL ? b.branchNameAr : b.branchName); row.getCell(4).value = b.totalValue; row.getCell(5).value = b.status; });
      ws.columns = [{ width: 18 }, { width: 14 }, { width: 28 }, { width: 16 }, { width: 10 }];
      const buf = await wb.xlsx.writeBuffer(); const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `OpeningBalances_${Date.now()}.xlsx`; a.click(); URL.revokeObjectURL(a.href);
      toast.success(isRTL ? 'تم التصدير بنجاح' : 'Exported successfully');
    } catch { toast.error(isRTL ? 'فشل التصدير' : 'Export failed'); }
  };

  const exportAllToPdf = async () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' }); const pw = doc.internal.pageSize.width;
      doc.setFillColor(240, 245, 255); doc.rect(0, 0, pw, 45, 'F');
      if (companyInfo?.logoBase64) { try { doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25); } catch {} }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(21, 101, 192); doc.text(compName || '', pw / 2, 15, { align: 'center' });
      doc.setFillColor(21, 101, 192); doc.roundedRect(pw / 2 - 50, 32, 100, 10, 3, 3, 'F');
      doc.setFontSize(12); doc.setTextColor(255, 255, 255); doc.text(pageTitle, pw / 2, 38.5, { align: 'center' });
      autoTable(doc, { head: [[isRTL ? 'رقم السند' : 'Document #', isRTL ? 'التاريخ' : 'Date', isRTL ? 'الموقع' : 'Location', isRTL ? 'القيمة' : 'Value', isRTL ? 'الحالة' : 'Status']],
        body: filteredBalances.map(b => [b.documentNumber, fmtDate(b.documentDate), b.warehouseName ? (isRTL ? (b.warehouseNameAr || b.warehouseName) : b.warehouseName) : (isRTL ? (b.branchNameAr || b.branchName || '') : (b.branchName || '')), fmt(b.totalValue), b.status]),
        startY: 50, theme: 'grid', headStyles: { fillColor: [21, 101, 192], textColor: [255, 255, 255], fontSize: 9, fontStyle: 'bold', halign: 'center' }, bodyStyles: { fontSize: 8 },
      });
      doc.save(`OpeningBalances_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(isRTL ? 'تم التصدير بنجاح' : 'Exported successfully');
    } catch { toast.error(isRTL ? 'فشل التصدير' : 'Export failed'); }
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ p: 2, borderRadius: 3, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' }}>
              <OpeningIcon sx={{ fontSize: 40 }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{t.title}</Typography>
              <Typography variant="body2" color="text.secondary">{t.subtitle}</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <IconButton onClick={loadData} disabled={loading}><RefreshIcon /></IconButton>
            <Button variant="contained" size="small" startIcon={<ExcelIcon />} onClick={exportAllToExcel} sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)', color: 'white' }}>Excel</Button>
            <Button variant="contained" size="small" startIcon={<PdfIcon />} onClick={exportAllToPdf} sx={{ background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)', color: 'white' }}>PDF</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}
              sx={{ background: 'linear-gradient(45deg, #10b981 30%, #059669 90%)', px: 3, py: 1.5 }}>
              {t.add}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
        <Card sx={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white' }}>
          <CardContent>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{stats.total}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.totalDocuments}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' }}>
          <CardContent>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{stats.posted}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.totalPosted}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white' }}>
          <CardContent>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{stats.draft}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.totalDraft}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', color: 'white' }}>
          <CardContent>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{stats.totalAmount.toFixed(3)}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.totalAmount}</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField fullWidth placeholder={t.search} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} elevation={3}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : filteredBalances.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Typography color="text.secondary">{t.noData}</Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#f1f5f9' }}>
                <TableCell sx={{ fontWeight: 700 }}>{t.documentNumber}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t.date}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t.location}</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">{t.totalValue}</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">{t.status}</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">{t.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBalances.map((balance) => (
                <TableRow key={balance.openingBalanceId} sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) } }}>
                  <TableCell><Typography fontWeight={600}>{balance.documentNumber}</Typography></TableCell>
                  <TableCell>{new Date(balance.documentDate).toLocaleDateString('en-GB')}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {balance.warehouseId ? (
                        <><WarehouseIcon fontSize="small" color="primary" />
                          <Typography>{isRTL ? balance.warehouseNameAr : balance.warehouseName}</Typography></>
                      ) : (
                        <><BranchIcon fontSize="small" color="secondary" />
                          <Typography>{isRTL ? balance.branchNameAr : balance.branchName}</Typography></>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={600} color="primary">{balance.totalValue.toFixed(3)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={balance.status === 'Posted' ? <PostedIcon /> : <DraftIcon />}
                      label={balance.status === 'Posted' ? t.posted : t.draft}
                      color={balance.status === 'Posted' ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title={isRTL ? 'طباعة' : 'Print'}>
                        <IconButton size="small" sx={{ color: '#FF9800' }} onClick={() => printBalance(balance)}>
                          <PrintIcon />
                        </IconButton>
                      </Tooltip>
                      {balance.status === 'Draft' ? (
                        <>
                          {canPostOpeningBalance() && (
                            <Tooltip title={t.post}>
                              <IconButton size="small" color="success" onClick={() => openConfirm('post', balance)}>
                                <PostIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canEditOpeningBalance(balance) && (
                            <>
                              <Tooltip title={t.edit}>
                                <IconButton size="small" color="primary" onClick={() => handleEdit(balance)}>
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t.delete}>
                                <IconButton size="small" color="error" onClick={() => openConfirm('delete', balance)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          {canPostOpeningBalance() && (
                            <Tooltip title={t.unpost}>
                              <IconButton size="small" color="warning" onClick={() => openConfirm('unpost', balance)}>
                                <UnpostIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Dialog */}
      <InventoryOpeningBalanceDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        openingBalance={selectedBalance}
        onSave={loadData}
      />

      {/* ✅ دايلوج التأكيد الموحد */}
      {confirmDialog.open && confirmDialog.balance && (() => {
        const cfg = confirmConfig[confirmDialog.action];
        return (
          <Dialog open={confirmDialog.open} onClose={closeConfirm} maxWidth="xs" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
            <DialogTitle sx={{
              background: `linear-gradient(135deg, ${cfg.gradientFrom} 0%, ${cfg.gradientTo} 100%)`,
              color: 'white', display: 'flex', alignItems: 'center', gap: 1,
            }}>
              {cfg.icon}
              <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
                {confirmDialog.balance.documentNumber}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, mt: 1 }}>
              <Typography>{cfg.message}</Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button onClick={closeConfirm} variant="outlined" sx={{ flex: 1 }}>{t.cancel}</Button>
              <Button onClick={handleConfirm} variant="contained" color={cfg.buttonColor} sx={{ flex: 1 }}>
                {cfg.buttonLabel}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}
    </Box>
  );
};

export default InventoryOpeningBalancesPage;
